// The half-float packer.
//
// WebGL2 uploads a `Float32Array` into an `R16F` or an `RGBA16F` texture and
// the driver converts. WebGPU does not: `queue.writeTexture` copies bytes,
// so a `r16float` plane must already hold 16-bit halves.
//
// `Float16Array` would do the work, but it lives in the ES2025 library and
// this project compiles against ES2022. So the conversion is written here
// as a pure function over the bit patterns, and a test pins it against the
// patterns the standard states.
//
// The rounding is round-to-nearest-even, which is what every GPU does.

/** One float32 as its half-float bit pattern. */
export function halfBits(v: number): number {
  F32[0] = v;
  const x = U32[0];
  const sign = (x >>> 16) & 0x8000;
  const exp = (x >>> 23) & 0xff;
  const frac = x & 0x7fffff;

  // Infinity and NaN keep their class. A NaN must stay a NaN, so a mantissa
  // that rounds to zero is given one bit back.
  if (exp === 0xff) return sign | 0x7c00 | (frac !== 0 ? 0x200 : 0);

  // The unbiased exponent, moved to the half's bias of 15.
  const e = exp - 127 + 15;

  // Above the half's range: the nearest value is infinity.
  if (e >= 0x1f) return sign | 0x7c00;

  // Normal.
  if (e > 0) {
    let bits = sign | (e << 10) | (frac >>> 13);
    // Round to nearest, ties to even. The dropped 13 bits decide.
    const rest = frac & 0x1fff;
    if (rest > 0x1000 || (rest === 0x1000 && (bits & 1) === 1)) bits++;
    return bits & 0xffff;
  }

  // Subnormal, or too small to hold at all. The implicit 1 comes back and
  // the whole mantissa shifts right by how far the exponent fell short.
  if (e < -10) return sign;
  const mant = frac | 0x800000;
  const shift = 14 - e;                       // 14 - e is 14 .. 24 here
  let bits = sign | (mant >>> shift);
  const rest = mant & ((1 << shift) - 1);
  const halfway = 1 << (shift - 1);
  if (rest > halfway || (rest === halfway && (bits & 1) === 1)) bits++;
  return bits & 0xffff;
}

/**
 * Packs `n` floats of `src` into `dst` as halves.
 *
 * The caller keeps `dst` between uploads, so a plane of 3.5 M floats packs
 * with no allocation at all.
 */
export function packF16(src: Float32Array, dst: Uint16Array, n: number, from = 0): void {
  const count = Math.min(n, src.length, dst.length);
  for (let i = Math.max(0, from); i < count; i++) dst[i] = halfBits(src[i]);
}

const BUF = new ArrayBuffer(4);
const F32 = new Float32Array(BUF);
const U32 = new Uint32Array(BUF);
